"use client";

import * as React from "react";
import { Box } from "../../components/ui/box.tsx";
import { useElementSize } from "../../hooks/use-element-size.ts";
import { cn } from "../../lib/utils.ts";
import { Image } from "../media/index.ts";
import {
  clampFocus,
  fittedSize,
  type ImageFit,
  NO_FOCUS,
  panFocus,
  type Point,
  type Size,
  zoomFocus,
} from "./zoom-math.ts";
import type { Omittable } from "../../lib/props.ts";

const DOUBLE_CLICK_ZOOM = 2;
// exp(-deltaY × this): lets a trackpad pinch (many small deltas) and a mouse wheel (few big ones) cover ground alike.
const WHEEL_PULL = 0.01;
// Long enough to span the gaps in one wheel stream, so the glide stays off for the whole gesture.
const WHEEL_SETTLE_MS = 150;

export type ZoomableImageProps = Omittable<{
  src: string;
  alt: string;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  maxZoom?: number;
  minZoom?: number;
  radius?: number;
  fit?: ImageFit;
}>;

export function ZoomableImage({
  src,
  alt,
  zoom,
  onZoomChange,
  maxZoom = 4,
  minZoom = 1,
  radius,
  fit = "contain",
}: ZoomableImageProps): React.ReactElement {
  const { ref, size: stage } = useElementSize<HTMLDivElement>();
  const [natural, setNatural] = React.useState<Size>({ width: 0, height: 0 });
  const [rawFocus, setFocus] = React.useState<Point>(NO_FOCUS);
  const [tracking, setTracking] = React.useState(false);
  const drag = React.useRef<{ x: number; y: number; focus: Point } | null>(null);

  const fitted = fittedSize(natural, stage, fit);
  const focus = clampFocus(rawFocus, fitted, stage, zoom);

  const zoomAbout = React.useCallback(
    (next: number, anchor: Point) => {
      const to = Math.min(maxZoom, Math.max(minZoom, next));
      setFocus(clampFocus(zoomFocus(focus, anchor, zoom, to), fitted, stage, to));
      onZoomChange(to);
    },
    [fitted, focus, maxZoom, minZoom, onZoomChange, stage, zoom],
  );

  const fromCenter = (clientX: number, clientY: number): Point => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return NO_FOCUS;
    return { x: clientX - rect.left - rect.width / 2, y: clientY - rect.top - rect.height / 2 };
  };

  // Wheel is native and non-passive: it must preventDefault so a pinch doesn't zoom the page.
  const pannable = fitted.width * zoom > stage.width + 0.5 || fitted.height * zoom > stage.height + 0.5;
  const latest = React.useRef({ zoom, focus, fitted, stage, zoomAbout, pannable });
  latest.current = { zoom, focus, fitted, stage, zoomAbout, pannable };
  const node = ref.current;
  React.useEffect(() => {
    if (!node) return undefined;
    let settle: ReturnType<typeof setTimeout> | undefined;
    const onWheel = (event: WheelEvent) => {
      const now = latest.current;
      const pinch = event.ctrlKey || event.metaKey;
      if (!pinch && !now.pannable) return;
      event.preventDefault();
      setTracking(true);
      clearTimeout(settle);
      settle = setTimeout(() => setTracking(false), WHEEL_SETTLE_MS);
      if (pinch) {
        const rect = node.getBoundingClientRect();
        now.zoomAbout(now.zoom * Math.exp(-event.deltaY * WHEEL_PULL), {
          x: event.clientX - rect.left - rect.width / 2,
          y: event.clientY - rect.top - rect.height / 2,
        });
      } else {
        setFocus(
          clampFocus(
            panFocus(now.focus, { x: -event.deltaX, y: -event.deltaY }, now.zoom),
            now.fitted,
            now.stage,
            now.zoom,
          ),
        );
      }
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      clearTimeout(settle);
      node.removeEventListener("wheel", onWheel);
    };
  }, [node]);

  return (
    <Box
      ref={ref}
      fill="size"
      overflow="hidden"
      position="relative"
      touchAction="none"
      cursor={pannable ? (tracking ? "grabbing" : "grab") : undefined}
      onDoubleClick={(event: React.MouseEvent) =>
        zoomAbout(zoom !== 1 ? 1 : DOUBLE_CLICK_ZOOM, fromCenter(event.clientX, event.clientY))
      }
      onPointerDown={(event: React.PointerEvent<HTMLDivElement>) => {
        if (!pannable) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { x: event.clientX, y: event.clientY, focus };
        setTracking(true);
      }}
      onPointerMove={(event: React.PointerEvent) => {
        const start = drag.current;
        if (!start) return;
        const delta = { x: event.clientX - start.x, y: event.clientY - start.y };
        setFocus(clampFocus(panFocus(start.focus, delta, zoom), fitted, stage, zoom));
      }}
      onPointerUp={() => {
        drag.current = null;
        setTracking(false);
      }}
      onPointerCancel={() => {
        drag.current = null;
        setTracking(false);
      }}
    >
      <Image
        alt={alt}
        draggable={false}
        src={src}
        onLoad={(event) =>
          setNatural({
            width: event.currentTarget.naturalWidth,
            height: event.currentTarget.naturalHeight,
          })
        }
        className={cn(
          "absolute origin-center select-none",
          !tracking && "transition-transform duration-200 ease-out motion-reduce:transition-none",
        )}
        style={{
          width: fitted.width,
          height: fitted.height,
          left: (stage.width - fitted.width) / 2,
          top: (stage.height - fitted.height) / 2,
          borderRadius: radius && radius / zoom,
          transform: `translate(${-focus.x * zoom}px, ${-focus.y * zoom}px) scale(${zoom})`,
        }}
      />
    </Box>
  );
}
